import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { authResetPassword } from '../api';
import { Lock, KeyRound, CheckCircle2 } from 'lucide-react';

const ResetPassword = () => {
  const { dir, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get('token') || '');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) {
      toast({ title: lang === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (pwd.length < 6) {
      toast({ title: lang === 'ar' ? 'كلمة المرور قصيرة (6 حروف أقل للأمان)' : 'Password too short (min 6 chars)', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await authResetPassword(token.trim(), pwd);
      setDone(true);
      toast({ title: lang === 'ar' ? 'تم تحديث كلمة المرور' : 'Password updated' });
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || (lang === 'ar' ? 'الرمز غير صالح أو منتهي' : 'Invalid or expired token');
      toast({ title: detail, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center noise-bg" dir={dir}>
      <div className="absolute inset-0 spot-light pointer-events-none" />
      <div className="max-w-md w-full mx-auto px-6 relative">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#2563eb]/15 border border-[#2563eb]/30 flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-[#3b82f6]" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}</h1>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 text-blue-400 mx-auto mb-3" />
              <div className="text-[#0a0a0a] font-medium">{lang === 'ar' ? 'تم بنجاح! يتم تحويلك لصفحة الدخول...' : 'Success! Redirecting to sign in...'}</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{lang === 'ar' ? 'رمز الاستعادة' : 'Reset Token'}</Label>
                <Input required value={token} onChange={(e) => setToken(e.target.value)} placeholder="..." className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827] font-mono text-xs" />
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{lang === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3" />
                  <PasswordInput required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{lang === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3" />
                  <PasswordInput required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="gold-button rounded-md h-12 w-full text-sm">{loading ? '…' : (lang === 'ar' ? 'تحديث كلمة المرور' : 'Update Password')}</Button>
              <p className="text-center text-xs text-[#111827]/60"><Link to="/login" className="text-[#3b82f6] hover:text-[#0a0a0a]">{lang === 'ar' ? 'العودة للدخول' : 'Back to Sign In'}</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
